import React, { useMemo } from 'react';
import { getToneLearningUnits, calculateUnitProgress } from '../data/toneLearningData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const ToneLearningUnitsPage = ({ language = 'traditional', onBack, onUnitClick }) => {
  const { data: remote } = useSupabaseData({
    table: 'eng_tonelearning',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const units = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return getToneLearningUnits();
    const rows = Array.isArray(remote) ? remote : [remote];
    const out = [];
    rows.filter(r => (r.type || r.section) === 'unit').forEach((r, idx) => {
      out.push({
        id: r.unit_id || r.id,
        icon: r.icon || '🎵',
        title: { tc: r.title_tc || '', sc: r.title_sc || '', en: r.title_en || '' },
        description: { tc: r.description_tc || '', sc: r.description_sc || '', en: r.description_en || '' },
        duration: r.duration || '15 min',
        totalLessons: r.total_lessons || 0,
        isUnlocked: r.is_unlocked !== false,
        isCompleted: Boolean(r.is_completed),
        difficulty: r.difficulty || 'beginner',
        topics: r.topics || []
      });
    });
    return out.length > 0 ? out : getToneLearningUnits();
  }, [remote]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/20 text-green-300';
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'advanced':
        return 'bg-red-500/20 text-red-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <h1 className="text-xl font-bold">
            {language === 'traditional' ? '語調學習單元' :
             language === 'simplified' ? '语调学习单元' :
             'Tone Learning Units'}
          </h1>
          <div className="w-8"></div> {/* Spacer for centering */}
        </div>

        {/* Description */}
        <div className="mb-6 text-center">
          <p className="text-gray-300 text-sm">
            {language === 'traditional' ? '選擇正確的語氣，掌握英語語調的精髓' :
             language === 'simplified' ? '选择正确的语气，掌握英语语调的精髓' :
             'Choose the right tone and master the essence of English intonation'}
          </p>
        </div>

        {/* Units Grid */}
        <div className="space-y-4 mb-20">
          {units.map((unit) => {
            const progress = calculateUnitProgress(unit.id);
            return (
              <div
                key={unit.id}
                onClick={() => unit.isUnlocked && onUnitClick(unit.id)}
                className={`bg-gray-800/30 border border-gray-600 rounded-xl p-4 cursor-pointer transition-all ${
                  unit.isUnlocked 
                    ? 'hover:bg-gray-700/50 hover:border-gray-500' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center space-x-4">
                  {/* Unit Icon */}
                  <div className="text-3xl flex-shrink-0">
                    {unit.icon}
                  </div>
                  
                  {/* Unit Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-white">
                        {getText(unit.title)}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(unit.difficulty)}`}>
                        {unit.difficulty.toUpperCase()}
                      </span>
                    </div>
                    
                    <p className="text-gray-400 text-sm mb-2">
                      {getText(unit.description)}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>⏱️ {unit.duration}</span>
                      <span>📚 {unit.totalLessons} {language === 'traditional' ? '課程' : language === 'simplified' ? '课程' : 'lessons'}</span>
                      {progress.totalLessons > 0 && (
                        <span>✅ {progress.completedLessons}/{progress.totalLessons}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress and Status */}
                  <div className="flex flex-col items-end space-y-2">
                    {unit.isUnlocked ? (
                      <>
                        {progress.totalLessons > 0 && (
                          <div className="flex items-center space-x-2">
                            <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${getProgressColor(progress.progress)} transition-all duration-300`}
                                style={{ width: `${progress.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400">{progress.progress}%</span>
                          </div>
                        )}
                        
                        {unit.isCompleted ? (
                          <div className="text-green-400 text-sm">✓ {language === 'traditional' ? '已完成' : language === 'simplified' ? '已完成' : 'Completed'}</div>
                        ) : (
                          <div className="text-blue-400 text-sm">→ {language === 'traditional' ? '開始學習' : language === 'simplified' ? '开始学习' : 'Start Learning'}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-500 text-sm">🔒 {language === 'traditional' ? '鎖定' : language === 'simplified' ? '锁定' : 'Locked'}</div>
                    )}
                  </div>
                </div>
                
                {/* Topics Preview */}
                {unit.isUnlocked && (
                  <div className="mt-3 pt-3 border-t border-gray-600">
                    <div className="flex flex-wrap gap-1">
                      {unit.topics.slice(0, 3).map((topic, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs"
                        >
                          {topic}
                        </span>
                      ))}
                      {unit.topics.length > 3 && (
                        <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs">
                          +{unit.topics.length - 3} {language === 'traditional' ? '更多' : language === 'simplified' ? '更多' : 'more'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ToneLearningUnitsPage;
